(() => {
  'use strict';

  const allowed = () => document.documentElement.dataset.cooperNativeEnabled === 'true';
  if (!allowed()) return;
  if (window.__cooperNativeMotion) {
    window.__cooperNativeMotion.refresh();
    return;
  }

  const root = document.documentElement;
  const preference = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const announcement = 'WHITE FRIDAY STARTS EARLY • UP TO 50% OFF';
  const selector = 'aside.announcement-bar, .header-logo, .hero-wrapper, .cooper-native-catalog, .cooper-native-model-grid, h2[data-cooper-category], h3[data-cooper-category]';
  const decorated = new Set();
  const seen = new WeakSet();
  const strips = new Map();
  const editorials = new Map();
  const motionClasses = ['cooper-motion-category', 'cooper-motion-story', 'cooper-motion-in-view'];
  let mutationObserver;
  let intersectionObserver;
  let frame = 0;
  let suspended = false;
  let destroyed = false;
  let paused = false;
  let labels = null;
  let launchElement = null;

  try {
    const config = document.getElementById('cooper-native-motion-config');
    labels = config ? JSON.parse(config.textContent) : null;
  } catch (_) {}
  if (!labels || ['pause', 'resume'].some((key) => typeof labels[key] !== 'string' || !labels[key].trim() || /translation missing|cooper_motion\.controls\./i.test(labels[key]))) labels = null;

  const reduced = () => preference ? preference.matches : true;
  const motionOff = () => reduced() || Boolean(window.Shopify?.designMode);

  function mark(element, className) {
    if (!element.classList.contains(className)) element.classList.add(className);
    decorated.add(element);
  }

  function observeDetail(element) {
    if (seen.has(element)) return;
    seen.add(element);
    if (intersectionObserver && !motionOff()) intersectionObserver.observe(element);
    else mark(element, 'cooper-motion-in-view');
  }

  function syncState() {
    if (destroyed) return;
    root.dataset.cooperNativeMotionState = motionOff() ? 'reduced' : suspended || document.hidden ? 'suspended' : paused ? 'paused' : 'running';
    strips.forEach(({ button, source, aside }) => {
      const off = motionOff();
      if (button.hidden !== off) button.hidden = off;
      const pressed = String(paused);
      const label = paused ? labels.resume : labels.pause;
      if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
      if (button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
      if (button.title !== label) button.title = label;
      const moving = source.textContent.trim() === announcement && !source.querySelector('a, button');
      if (aside.dataset.cooperMoving !== String(moving)) aside.dataset.cooperMoving = String(moving);
    });
  }

  function addAnnouncement(aside) {
    const source = aside.querySelector('.announcement-bar__text');
    if (!labels?.pause || !labels?.resume || !source || source.textContent.trim() !== announcement) return;
    if (aside.querySelectorAll('.announcement-bar__text').length !== 1 || aside.querySelector('a, button:not(.cooper-motion-toggle)')) return;
    const existing = strips.get(aside);
    if (existing?.source === source && existing.viewport.isConnected && existing.button.isConnected) return;
    if (existing) removeStrip(existing);
    aside.querySelectorAll('.cooper-motion-marquee, .cooper-motion-toggle').forEach((element) => element.remove());

    const viewport = document.createElement('div');
    viewport.className = 'cooper-motion-marquee';
    viewport.setAttribute('aria-hidden', 'true');
    viewport.dir = 'ltr';
    const track = document.createElement('div');
    track.className = 'cooper-motion-marquee__track';
    for (let groupIndex = 0; groupIndex < 2; groupIndex += 1) {
      const group = document.createElement('div');
      group.className = 'cooper-motion-marquee__group';
      for (let index = 0; index < 4; index += 1) {
        const item = document.createElement('span');
        item.className = 'cooper-motion-marquee__item';
        item.textContent = announcement;
        group.append(item);
      }
      track.append(group);
    }
    viewport.append(track);

    const button = document.createElement('button');
    button.className = 'cooper-motion-toggle';
    button.type = 'button';
    button.lang = labels.language || document.documentElement.lang;
    const icon = document.createElement('span');
    icon.className = 'cooper-motion-toggle__icon';
    icon.setAttribute('aria-hidden', 'true');
    button.append(icon);
    button.addEventListener('click', () => {
      paused = !paused;
      syncState();
    });

    aside.append(viewport, button);
    source.classList.add('cooper-motion-announcement-source');
    aside.classList.add('cooper-motion-announcement');
    strips.set(aside, { aside, source, viewport, button });
  }

  function removeStrip(strip) {
    strip.viewport.remove();
    strip.button.remove();
    strip.source.classList.remove('cooper-motion-announcement-source');
    strip.aside.classList.remove('cooper-motion-announcement');
    delete strip.aside.dataset.cooperMoving;
    strips.delete(strip.aside);
  }

  function addDetails() {
    const main = document.getElementById('MainContent');
    if (!main) {
      editorials.forEach((record) => restoreEditorial(record, false));
      launchElement?.remove();
      return;
    }
    const directHeroes = [...main.querySelectorAll(':scope > .hero-wrapper')];
    const firstHero = directHeroes.find((hero) => !editorials.has(hero));
    editorials.forEach((record, hero) => {
      if (!record.marker.isConnected && !hero.isConnected) editorials.delete(hero);
    });
    directHeroes.filter((hero) => hero !== firstHero && !editorials.has(hero)).forEach((hero) => {
      const marker = document.createComment('');
      hero.before(marker);
      editorials.set(hero, { node: hero, marker, parent: main, hidden: hero.getAttribute('hidden') });
    });
    const records = [...editorials.values()].filter((record) => record.parent === main && record.marker.isConnected);
    records.forEach(({ node: hero }) => {
      mark(hero, 'cooper-motion-story');
      observeDetail(hero);
    });
    const wordmark = document.querySelector('.header-logo [data-testid="header-logo"]')?.textContent.trim();
    if (firstHero && wordmark && !main.querySelector('#cooper-motion-divider')) {
      const divider = document.createElement('div');
      divider.id = 'cooper-motion-divider';
      divider.className = 'cooper-motion-divider';
      divider.setAttribute('aria-hidden', 'true');
      divider.dir = 'ltr';
      const left = document.createElement('span');
      const brand = document.createElement('span');
      const right = document.createElement('span');
      left.className = right.className = 'cooper-motion-divider__line';
      brand.className = 'cooper-motion-divider__brand';
      brand.textContent = wordmark;
      divider.append(left, brand, right);
      firstHero.append(divider);
      observeDetail(divider);
    }
    const divider = main.querySelector('#cooper-motion-divider');
    if (firstHero && divider && divider.parentElement !== firstHero) firstHero.append(divider);
    if (divider) observeDetail(divider);
    const launch = addLaunch(main, main.querySelector('.cooper-native-catalog'));
    placeCatalogDividers(main, records, launch);
    main.querySelectorAll('.cooper-native-model-grid > h2[data-cooper-category], .cooper-native-model-grid > h3[data-cooper-category]').forEach((heading) => {
      mark(heading, 'cooper-motion-category');
      observeDetail(heading);
    });
  }

  function setHidden(element, hidden) {
    if (element.hidden !== hidden) element.hidden = hidden;
  }

  function restoreEditorial(record, hide) {
    const { node, marker } = record;
    if (marker.isConnected && marker.nextSibling !== node) marker.after(node);
    node.classList.remove('cooper-motion-catalog-divider');
    if (hide) setHidden(node, true);
    else if (record.hidden === null) node.removeAttribute('hidden');
    else if (node.getAttribute('hidden') !== record.hidden) node.setAttribute('hidden', record.hidden);
  }

  function placeCatalogDividers(main, records, launch) {
    const catalog = main.querySelector('.cooper-native-catalog');
    const panel = catalog?.querySelector('.cooper-native-model-grid');
    const active = allowed() && Boolean(catalog);
    const cards = panel ? [...panel.querySelectorAll(':scope > cooper-model-card[data-cooper-category]')] : [];
    const groups = panel && !panel.hidden && !catalog.hidden ? [...panel.querySelectorAll(':scope > h2[data-cooper-category], :scope > h3[data-cooper-category]')].filter((heading) => !heading.hidden && cards.some((card) => !card.hidden && card.dataset.cooperCategory === heading.dataset.cooperCategory)) : [];
    const boundaries = groups.slice(1);
    const used = new Set();
    const targets = new Map();
    function allocate(element, preferences) {
      if (!element || !boundaries.length) return;
      const target = preferences.map((category) => boundaries.find((heading) => heading.dataset.cooperCategory === category && !used.has(heading))).find(Boolean) || boundaries.find((heading) => !used.has(heading));
      if (target) { used.add(target); targets.set(element, target); }
    }
    if (active && panel && !panel.hidden && !catalog.hidden) {
      allocate(launch, ['halfzip', 'fullzip', 'round']);
      if (records[0]) allocate(records[0].node, ['hoodie', 'sweatshirt']);
      if (records[1]) allocate(records[1].node, ['fullzip', 'round']);
      records.slice(2).forEach((record) => allocate(record.node, []));
    }
    records.forEach((record) => {
      if (!targets.has(record.node)) restoreEditorial(record, active);
    });
    if (launch && !targets.has(launch)) {
      launch.remove();
      launch.classList.remove('cooper-motion-catalog-divider');
      setHidden(launch, true);
    }
    targets.forEach((heading, element) => {
      if (element.nextElementSibling !== heading || element.parentElement !== panel) heading.before(element);
      setHidden(element, false);
      element.classList.add('cooper-motion-catalog-divider');
      observeDetail(element);
    });
  }

  function catalogDestination(catalog) {
    const value = catalog?.dataset.cooperAllUrl;
    if (!value || !value.startsWith('/') || value.startsWith('//')) return null;
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin || !/\/collections\/[^/]+\/?$/.test(url.pathname) || url.search || url.hash) return null;
      return url.pathname;
    } catch (_) { return null; }
  }

  function addLaunch(main, catalog) {
    const copy = labels?.launch;
    const destination = catalogDestination(catalog);
    if (!destination || !copy || copy.heading !== 'WE’RE ONLINE. LET’S CELEBRATE.' || copy.subtitle !== 'UP TO 50% OFF THE NEW COLLECTION' || copy.cta !== 'SHOP NOW') {
      launchElement?.remove();
      return null;
    }
    let launch = main.querySelector('#cooper-motion-launch') || launchElement;
    if (!launch) {
      launch = document.createElement('section');
      launch.id = 'cooper-motion-launch';
      launch.className = 'cooper-motion-launch';
      launch.lang = 'en';
      launch.dir = 'ltr';
      launch.setAttribute('aria-labelledby', 'cooper-motion-launch-title');
      const inner = document.createElement('div');
      inner.className = 'cooper-motion-launch__inner';
      const text = document.createElement('div');
      text.className = 'cooper-motion-launch__copy';
      const heading = document.createElement('h2');
      heading.id = 'cooper-motion-launch-title';
      heading.className = 'cooper-motion-launch__heading';
      heading.textContent = copy.heading;
      const subtitle = document.createElement('p');
      subtitle.className = 'cooper-motion-launch__subtitle';
      subtitle.textContent = copy.subtitle;
      const link = document.createElement('a');
      link.className = 'cooper-motion-launch__cta';
      link.setAttribute('href', destination);
      link.textContent = copy.cta;
      text.append(heading, subtitle);
      inner.append(text, link);
      launch.append(inner);
    }
    launchElement = launch;
    const link = launch.querySelector('.cooper-motion-launch__cta');
    if (link?.getAttribute('href') !== destination) link.setAttribute('href', destination);
    observeDetail(launch);
    return launch;
  }

  function observeChanges() {
    if (mutationObserver && !suspended) {
      mutationObserver.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['hidden', 'data-cooper-all-url'] });
      mutationObserver.observe(root, { attributes: true, attributeFilter: ['data-cooper-native-enabled'] });
    }
  }

  function refresh() {
    frame = 0;
    if (destroyed || suspended || !document.body) return;
    if (!allowed()) {
      destroy();
      return;
    }
    mutationObserver?.disconnect();
    root.dataset.cooperNativeMotion = 'true';
    strips.forEach((strip) => {
      if (!strip.aside.isConnected) removeStrip(strip);
    });
    decorated.forEach((element) => {
      if (!element.isConnected) {
        decorated.delete(element);
        intersectionObserver?.unobserve(element);
      }
    });
    document.querySelectorAll('aside.announcement-bar').forEach(addAnnouncement);
    addDetails();
    syncState();
    observeChanges();
  }

  function queue() {
    if (!destroyed && !suspended && !frame) frame = window.requestAnimationFrame(refresh);
  }

  function stop() {
    suspended = true;
    if (frame) window.cancelAnimationFrame(frame);
    frame = 0;
    mutationObserver?.disconnect();
    syncState();
  }

  function resume() {
    suspended = false;
    queue();
  }

  function preferenceChanged() {
    syncState();
    if (motionOff()) {
      intersectionObserver?.disconnect();
      decorated.forEach((element) => mark(element, 'cooper-motion-in-view'));
      const divider = document.getElementById('cooper-motion-divider');
      if (divider) mark(divider, 'cooper-motion-in-view');
    }
  }

  function destroy() {
    if (destroyed) return;
    stop();
    destroyed = true;
    intersectionObserver?.disconnect();
    strips.forEach(removeStrip);
    editorials.forEach((record) => { restoreEditorial(record, false); record.marker.remove(); });
    editorials.clear();
    decorated.forEach((element) => element.classList.remove(...motionClasses));
    document.getElementById('cooper-motion-divider')?.remove();
    launchElement?.remove();
    launchElement = null;
    delete root.dataset.cooperNativeMotion;
    delete root.dataset.cooperNativeMotionState;
    document.removeEventListener('shopify:section:load', queue);
    document.removeEventListener('visibilitychange', syncState);
    document.removeEventListener('DOMContentLoaded', start);
    preference?.removeEventListener?.('change', preferenceChanged);
    window.removeEventListener('pagehide', stop);
    window.removeEventListener('pageshow', resume);
    delete window.__cooperNativeMotion;
  }

  function start() {
    if (destroyed || !allowed()) return;
    if ('IntersectionObserver' in window) {
      intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || suspended) return;
          mark(entry.target, 'cooper-motion-in-view');
          intersectionObserver.unobserve(entry.target);
        });
      }, { threshold: 0.15 });
    }
    if ('MutationObserver' in window) {
      mutationObserver = new MutationObserver((records) => {
        if (records.some((record) => record.type === 'attributes' ? record.target === root || record.target.matches('.cooper-native-catalog, .cooper-native-model-grid, h2[data-cooper-category], h3[data-cooper-category], cooper-model-card[data-cooper-category]') : (record.target.nodeType === 1 ? record.target : record.target.parentElement)?.closest('aside.announcement-bar') || [...record.addedNodes, ...record.removedNodes].some((node) => node.nodeType === 1 && (node.matches?.(selector) || node.querySelector?.(selector))))) queue();
      });
    }
    document.addEventListener('shopify:section:load', queue);
    document.addEventListener('visibilitychange', syncState);
    preference?.addEventListener?.('change', preferenceChanged);
    window.addEventListener('pagehide', stop);
    window.addEventListener('pageshow', resume);
    refresh();
  }

  window.__cooperNativeMotion = { refresh, destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
